/**
 * Public bakery site: avoid showing server internals, SQL, stack hints, or HTML error pages to shoppers.
 * When we cannot show a safe API line, we still tell the customer something failed — politely — and
 * give a clear human fallback (call Tammy) using the same default number as Settings / Contact.
 *
 * Staff portal (`/admin/*`) should keep surfacing real API messages — do not use this helper there.
 */

/** Public line on site and in Settings fallback; used when contact API is unavailable in error paths. */
const TAMMY_PHONE_DISPLAY = '(705) 503-4966';

const GENERIC_ACTION = `We're sorry — the website hit a snag. Please call Tammy at ${TAMMY_PHONE_DISPLAY} to place your order, and let her know the online page had trouble so we can fix it. Thank you for your patience.`;

const GENERIC_LOAD = `We're sorry — this page didn't load properly. Try refreshing. If it keeps happening, call Tammy at ${TAMMY_PHONE_DISPLAY} and tell her which page failed so we can help. Thank you for your patience.`;

/** Heuristic: strings that look like infrastructure / DB errors, not written for customers. */
const LOOKS_INTERNAL =
  /relation\b|column\b|constraint\b|violates|ECONNREFUSED|supabase|postgres|nginx|<!doctype|\bstack\b|at \w+\.|undefined is not|internal server|PGRST|rpc\b/i;

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isSafeEcho(msg: string): boolean {
  const t = msg.trim();
  if (t.length < 1 || t.length > 300) return false;
  if (LOOKS_INTERNAL.test(t)) return false;
  return true;
}

/** Shoppers browsing menu, guides, collections, etc. */
export function publicLoadErrorMessage(): string {
  return GENERIC_LOAD;
}

/** Checkout, contact form, payment — generic when we cannot show a safe API line. */
export function publicActionErrorMessage(): string {
  return GENERIC_ACTION;
}

/**
 * Map HTTP status + JSON body from our APIs to a single UI string.
 * - 4xx: use `error` or `message` when it looks customer-safe (short, no leak patterns).
 * - 503 + SQUARE_ORDER_PENDING: allow the known “wait and retry Pay” copy from the backend.
 * - Otherwise: generic action message (includes call Tammy).
 */
export function publicMessageFromApiErrorResponse(status: number, body: unknown): string {
  if (status === 503 && isRecord(body) && body.code === 'SQUARE_ORDER_PENDING') {
    const err = typeof body.error === 'string' ? body.error.trim() : '';
    if (err && isSafeEcho(err)) return err;
  }
  // Never echo API `error` text for 5xx — those responses can include half-generic + leaked `detail`.
  if (status >= 500) {
    return GENERIC_ACTION;
  }
  if (status >= 400 && status < 500 && isRecord(body)) {
    const err = typeof body.error === 'string' ? body.error.trim() : '';
    const msg = typeof body.message === 'string' ? body.message.trim() : '';
    const candidate = err || msg;
    if (candidate && isSafeEcho(candidate)) return candidate;
  }
  return GENERIC_ACTION;
}

/** Sanitize a string already intended for UI (e.g. from a child callback). */
export function publicGuardUserMessage(text: string): string {
  return publicCatchMessage(new Error(text));
}

/**
 * For `catch` around fetch/checkout: use Error.message only when it is clearly customer-facing
 * (same rules as API echo). Network failures and unknown errors map to the generic action line.
 */
export function publicCatchMessage(err: unknown): string {
  if (!(err instanceof Error)) return GENERIC_ACTION;
  const m = err.message.trim();
  if (!m) return GENERIC_ACTION;
  if (LOOKS_INTERNAL.test(m)) return GENERIC_ACTION;
  if (/failed to fetch|networkerror|load failed|aborted|timeout/i.test(m)) return GENERIC_ACTION;
  if (m.length > 300) return GENERIC_ACTION;
  return m;
}
